import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Tabs as MuiTabs, Tab, Box, Stack } from '@mui/material';
import { useLocalization } from 'localization';

interface TabItem {
  label: React.ReactNode;
  key: string;
  children: React.ReactNode;
}

type Props = Partial<{
  tabItems: TabItem[];
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  onChange?: (tabKey: string) => void;
  defaultActiveKey: string;
}>;

const Tabs: React.FC<Props> = props => {
  const { tabItems = [], onChange, rightContent, defaultActiveKey } = props;
  const navigate = useNavigate();
  const { t } = useLocalization();
  const location = useLocation();

  const currentActiveKey = new URLSearchParams(location.search).get('tab') ?? defaultActiveKey ?? tabItems[0].key;

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    navigate(`?tab=${newValue}`, { replace: true });
    onChange?.(newValue);
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between">
        <MuiTabs
          value={currentActiveKey}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ flexGrow: 1 }}
        >
          {tabItems.map((tab, index) => (
            <Tab
              key={tab.key ?? index}
              label={typeof tab.label === 'string' ? t(tab.label) : tab.label}
              value={tab.key ?? index}
            />
          ))}
        </MuiTabs>
        <Box>{rightContent}</Box>
      </Stack>
      <Box sx={{ mt: 2 }}>
        {tabItems.map((tab, index) => (
          <Box key={tab.key ?? index} role="tabpanel" hidden={currentActiveKey !== tab.key}>
            {currentActiveKey === tab.key && tab.children}
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

export default Tabs;
